<script setup>
import {defineProps, ref} from "vue";
import NextButton from "../NextButton.vue";
import Lock from "../../../../../../franchise-listings/assets/svg/LockSimple.svg";
import PopperContainer from "../../../../../../franchise-listings/components/MainListing/PopperContainer.vue";
import Capitals from "../../../../data/capitals.json";
import Countries from "../../../../data/countries.json";
import Timeframes from "../../../../data/timeframe.json";

const {flowState} = defineProps(['visible', 'flowState']);

const errors = ref({
  phoneNumber: false,
  country: false,
  capital: false,
  timeframe: false,
  fullPhoneNumber: false,
});

const onPhoneNumberChange = (e) => {
  flowState.setFormValue('phoneNumber', e.target.value.trim());
}

// Validations
let onlyNumbers = /^\d+$/;

const checkInvalid = (field) => {
  // Get field value
  const fieldData = flowState.value[field];

  // Validate fields
  if (field === 'phoneNumber') {
    if (fieldData === '') {
      errors.value[field] = 'Field is required';
    } else if (!onlyNumbers.test(fieldData)) {
      errors.value[field] = 'Enter only numbers';
    } else {
      errors.value[field] = false;
    }
  } else if (field === 'country') {
    // Check if country is selected
    if (fieldData === null || fieldData === '') {
      errors.value[field] = 'Field is required';
    } else {
      errors.value[field] = false;
    }
  }
}

function loadCountries() {
  let new_array = [];
  $.each(Countries, function (key, value) {
    new_array.push(value)
  });

  return new_array;
}

let countries = loadCountries();

// Set phone number value
const changePhoneNumber = () => {
  // if phone number and country set, set full phone number for twilio verification
  if (flowState.value.phoneNumber !== null && flowState.value.country !== null) {

    let country = Countries.filter(
        function (Countries) {
          return Countries.name === flowState.value.country
        }
    );

    if (country.length > 0) {
      flowState.value.fullPhoneNumber = country[0].phone_code + flowState.value.phoneNumber;
    }

    if (flowState.value.fullPhoneNumber !== flowState.getPrevPhoneNumber()) {
      flowState.setPrevPhoneNumber(flowState.value.fullPhoneNumber)
      flowState.setVerifyAttempts(1);
    }
  }
}

const changeCountry = () => {
  // Check if capital is selected
  if (flowState.value.country === null) {
    errors.value['country'] = 'Field is required';
  } else {
    errors.value['country'] = false;

    // Set full phone number with country code
    changePhoneNumber();
  }
}

const changeCapital = () => {
  // Check if capital is selected
  if (flowState.value.liquidCapital === null) {
    errors.value['capital'] = 'Field is required';
  } else {
    errors.value['capital'] = false;
  }
}

const changeTimeframe = () => {
	// Check if timeframe is selected
	if (flowState.value.timeframe === '' || flowState.value.timeframe === null) {
		errors.value['timeframe'] = 'Field is required';
	} else {
		errors.value['timeframe'] = false;
	}
}

const checkErrors = () => {
  return errors.value.phoneNumber === false
      && errors.value.country === false
      && errors.value.capital === false
      && errors.value.timeframe === false
}

const notNullValues = () => {
  return flowState.value.phoneNumber !== null &&
      flowState.value.fullPhoneNumber !== null &&
      flowState.value.country !== null &&
      flowState.value.liquidCapital !== null &&
      flowState.value.timeframe !== null
}

const stepIsReady = () => {
  // Check if all fields are filled and valid
  return checkErrors() && notNullValues();
}

const sendVerificationCode = async () => {
  // Hide button and show loader
  $('.btn-next-orange').hide();
  $('#loadingBtn').show();

  // Clear error message
  $('#showPhoneErrorMessage').hide();

  try {
    // Validate phone number and send verification code
    await flowState.services.verifySms(flowState.fullPhoneNumber(), function (response) {
      flowState.setPhoneNumberValidationRequestId(response['request_id']);

      // Start countdown after SMS sent
      flowState.startCountdown();

      flowState.next();
    });

  } catch (e) {
    console.log(e)
  }
};

</script>

<template>
  <div v-if="visible">
    <div class="tab">
	    <div class="row my-4">
		    <h5 class="tab-title">Access the <strong>free</strong> business plan and forecasting model:</h5>
	    </div>
      <div class="row mt-0 mb-0">
        <div class="col-12 col-md-6">
          <p class="small-text-normal mb-1">
            Country code
            <span class="text-danger ms-1">*
              <PopperContainer
                  :title="'Why do we need to verify your phone number?'"
                  :content="'Based on your request, you may be reached out to via phone.'"
              />
            </span>
          </p>
          <v-select :options="countries"
                    label="name"
                    class="country-select"
                    id="country"
                    v-model="flowState.value.country"
                    :reduce="country => country.name"
                    @focusout="changeCountry">
            <template #option="option">
	            <span>{{ option.emoji }} {{ option.name }} <strong class="fw-normal text-black-50 fs-5">{{ '(' + option.phone_code + ')' }}</strong></span>
            </template>
          </v-select>
          <span v-if="errors.country" class="validationMessage text-danger ps-2 pe-2 m-0">
            {{ errors.country }}
          </span>
        </div>
        <div class="col-12 col-md-6">
          <p class="small-text-normal mb-1 mt-3 mt-md-0">Phone number <span class="text-danger ms-1">*</span>
          </p>
          <p>
            <input
                maxlength="10"
                type="text"
                placeholder="1234567890"
                :value="flowState.value.phoneNumber"
                @change="onPhoneNumberChange"
                @blur="checkInvalid('phoneNumber')"
                @focusout="changePhoneNumber"
            />
            <span v-if="errors.phoneNumber" class="validationMessage text-danger ps-2 pe-2 m-0">
              {{ errors.phoneNumber }}
            </span>
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <p class="small-text-normal mb-1">
            Liquid capital available
            <span class="text-danger ms-1">*
              <PopperContainer :title="'Why do we ask liquid capital available? '"
                               :content="'Different franchises have different levels of required liquid capital. As a general rule, the more liquid capital available, the greater the number of franchises you may be eligible to invest in.'"/>
            </span>
          </p>
          <v-select :options="Capitals"
                    label="capital"
                    v-model="flowState.value.liquidCapital"
                    :reduce="capital => capital.capital"
                    @focusout="changeCapital">
          </v-select>
          <span v-if="errors.capital" class="validationMessage text-danger ps-2 pe-2 m-0">
            {{ errors.capital }}
          </span>
        </div>
        <div class="col-12">
	        <p class="small-text-normal mb-1 mt-3">
		        Desired timeframe
		        <span class="text-danger ms-1">*
		          <PopperContainer
			          :content="'We ask the timeframe to ensure that we meet your expectations as best we can.'"/>
		        </span>
	        </p>
	        <v-select :options="Timeframes"
	                  label="timeframe"
	                  v-model="flowState.value.timeframe"
	                  :reduce="timeframe => timeframe.timeframe"
	                  @focusout="changeTimeframe">
	        </v-select>
	        <span v-if="errors.timeframe" class="validationMessage text-danger ps-2 pe-2 m-0">
            {{ errors.timeframe }}
          </span>
        </div>
      </div>
			<div id="showPhoneErrorMessage" class="row p-3 mt-3 pb-0">
        <div class="col col-12 p-3 mb-0">
          <p class="mb-0 d-flex justify-content-between align-items-center">
            <Lock/>
            We could not send a verification text to the phone number provided. Please correct your phone number and
            resubmit.
          </p>
        </div>
      </div>
    </div>

    <div>
      <NextButton
          :text="flowState.nextButtonText()"
          :enabled="stepIsReady()"
          @click="() => { sendVerificationCode() }"
      />
      <div id="loadingBtn" class="mt-4">
        <span id="loader"></span>
      </div>
    </div>
  </div>
</template>
